import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { assessmentUpdateNotifier } from '../utils/assessmentUpdateNotifier';

interface AssessmentResponse {
  questionId: string;
  answer: number | number[] | string;
  timestamp: Date;
}

interface SpiritualData {
  chronicIllness: boolean;
  assessmentType: 'fasdl' | 'facit-sp-12';
}

interface AnswerOption {
  value: string;
  label: string;
}

const SpiritualAssessmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [assessmentType, setAssessmentType] = useState<'fasdl' | 'facit-sp-12'>('fasdl');
  const [isLoading, setIsLoading] = useState(false);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [spiritualData, setSpiritualData] = useState<SpiritualData>({
    chronicIllness: false,
    assessmentType: 'fasdl'
  });
  
  // Determine total questions based on assessment type (1 selection + actual questions)
  const totalQuestions = assessmentType === 'fasdl' ? 11 : 13; // 1 selection + 10 FASDL or 1 selection + 12 FACIT
  
  // Get display totals for question numbering
  const getDisplayTotal = () => {
    if (currentQuestion === 1) {
      return 1; // Selection question shows "1 of 1"
    }
    return assessmentType === 'fasdl' ? 10 : 12; // Actual assessment questions show "1 of 10" or "1 of 12"
  };
  
  const getDisplayQuestionNumber = () => {
    if (currentQuestion === 1) {
      return 1; // Selection question
    }
    return currentQuestion - 1; // Actual assessment questions start from 1
  };

  // Assessment Tool Selection Question
  const assessmentSelectionQuestion = {
    id: '0',
    text: 'Which spiritual assessment would you like to take?',
    type: 'selection' as const,
    options: [
      'Assessment Tool 1.1 - FASDL (Functional Assessment of Spirituality in Daily Living)',
      'Assessment Tool 1.2 - FACIT-Sp-12 (For individuals with chronic illness)'
    ]
  };

  // FASDL Questions (Assessment Tool 1.1 - 10 items)
  const fasdlQuestions = [
    {
      id: '1',
      text: 'I feel a deep sense of connection with other people.',
      type: 'likert' as const,
      scale: 'fasdl',
      domain: 'Connectedness'
    },
    {
      id: '2',
      text: 'I feel connected to all living things.',
      type: 'likert' as const,
      scale: 'fasdl',
      domain: 'Connectedness'
    },
    {
      id: '3',
      text: 'I take time to be fully present in the moment.',
      type: 'likert' as const,
      scale: 'fasdl',
      domain: 'Mindfulness'
    },
    {
      id: '4',
      text: 'I reflect on my life experiences with awareness.',
      type: 'likert' as const,
      scale: 'fasdl',
      domain: 'Mindfulness'
    },
    {
      id: '5',
      text: 'I respond with compassion when others are in need.',
      type: 'likert' as const,
      scale: 'fasdl',
      domain: 'Compassion'
    },
    {
      id: '6',
      text: 'I try to be understanding toward people who hurt me.',
      type: 'likert' as const,
      scale: 'fasdl',
      domain: 'Compassion'
    },
    {
      id: '7',
      text: 'I often feel grateful for the life I have.',
      type: 'likert' as const,
      scale: 'fasdl',
      domain: 'Gratitude'
    },
    {
      id: '8',
      text: 'I express appreciation for the kindness of others.',
      type: 'likert' as const,
      scale: 'fasdl',
      domain: 'Gratitude'
    },
    {
      id: '9',
      text: 'I feel a sense of inner peace even when life is difficult.',
      type: 'likert' as const,
      scale: 'fasdl',
      domain: 'Inner Peace'
    },
    {
      id: '10',
      text: 'I try to live my life in a way that brings harmony and balance.',
      type: 'likert' as const,
      scale: 'fasdl',
      domain: 'Inner Peace'
    }
  ];

  // FACIT-Sp-12 Questions (Assessment Tool 1.2 - 12 items)
  const facitQuestions = [
    {
      id: '1',
      text: 'I feel peaceful.',
      type: 'likert' as const,
      scale: 'facit'
    },
    {
      id: '2',
      text: 'I have a reason for living.',
      type: 'likert' as const,
      scale: 'facit'
    },
    {
      id: '3',
      text: 'My life has been productive.',
      type: 'likert' as const,
      scale: 'facit'
    },
    {
      id: '4',
      text: 'I feel a sense of harmony within myself.',
      type: 'likert' as const,
      scale: 'facit'
    },
    {
      id: '5',
      text: 'My life lacks meaning or purpose.',
      type: 'likert' as const,
      scale: 'facit',
      reverse: true
    },
    {
      id: '6',
      text: 'I have trouble feeling peace of mind.',
      type: 'likert' as const,
      scale: 'facit'
    },
    {
      id: '7',
      text: 'I find strength in my faith or spiritual beliefs.',
      type: 'likert' as const,
      scale: 'facit'
    },
    {
      id: '8',
      text: 'My illness or challenges have strengthened my faith or spiritual beliefs.',
      type: 'likert' as const,
      scale: 'facit'
    },
    {
      id: '9',
      text: 'I find comfort in my faith or spiritual beliefs.',
      type: 'likert' as const,
      scale: 'facit'
    },
    {
      id: '10',
      text: 'I feel a sense of purpose in my life.',
      type: 'likert' as const,
      scale: 'facit'
    },
    {
      id: '11',
      text: 'I know that whatever happens with my life, things will be okay.',
      type: 'likert' as const,
      scale: 'facit'
    },
    {
      id: '12',
      text: 'I am able to reach down deep into myself for comfort.,',
      type: 'likert' as const,
      scale: 'facit'
    }
  ];

  const getCurrentQuestion = () => {
    if (currentQuestion === 1) {
      return assessmentSelectionQuestion; // Assessment tool selection
    }
    
    if (assessmentType === 'fasdl') {
      // For FASDL, questions 2-11 correspond to indices 0-9
      return fasdlQuestions[currentQuestion - 2];
    } else {
      // For FACIT-Sp-12, questions 2-13 correspond to indices 0-11
      return facitQuestions[currentQuestion - 2];
    }
  };

  const getAnswerOptions = (): AnswerOption[] | string[] => {
    const question = getCurrentQuestion();

    if (!question) {
      return [];
    }

    if (question.type === 'selection') {
      return question.options;
    }

    if (question.scale === 'fasdl') {
      // FASDL uses 1-5 scale - return objects with value and label
      return [
        { value: '1', label: 'Not at all true of me' },
        { value: '2', label: 'A little true of me' },
        { value: '3', label: 'Somewhat true of me' },
        { value: '4', label: 'Mostly true of me' },
        { value: '5', label: 'Very true of me' }
      ];
    }

    if (question.scale === 'facit') {
        // FACIT-Sp-12 default choices
        // For question #6, use custom mapping
        if (question.id === '6') {
          return [
            { value: '4', label: 'Not at all' },
            { value: '3', label: 'A little bit' },
            { value: '2', label: 'Somewhat' },
            { value: '2', label: 'Quite a bit' },
            { value: '0', label: 'Very much' }
          ];
        }
        return [
          { value: '0', label: 'Not at all' },
          { value: '1', label: 'A little bit' },
          { value: '2', label: 'Somewhat' },
          { value: '3', label: 'Quite a bit' },
          { value: '4', label: 'Very much' }
        ];
    }

    return [];
  };

  const saveCurrentResponse = (answer: number | number[] | string) => {
    const question = getCurrentQuestion();
    const newResponse: AssessmentResponse = {
      questionId: question.id,
      answer: answer,
      timestamp: new Date()
    };

    setResponses(prev => {
      const filtered = prev.filter(r => r.questionId !== question.id);
      return [...filtered, newResponse];
    });

    // If this is the assessment selection question, immediately apply the selection
    if (question.id === '0') {
      // Determine selected tool from selection index
      const selectionIndex = typeof answer === 'number' ? answer : parseInt(String(answer), 10);
      const selectedTool = selectionIndex === 0 ? 'fasdl' : 'facit-sp-12';

      setSpiritualData({ 
        chronicIllness: selectedTool === 'facit-sp-12',
        assessmentType: selectedTool
      });
      setAssessmentType(selectedTool);

      // Advance to first actual question (questions start at 2)
      setCurrentQuestion(2);
    }
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions) {
      // Handle assessment tool selection
      if (currentQuestion === 1) {
        const selectionResponse = responses.find(r => r.questionId === '0');
        if (selectionResponse) {
          const selectedTool = selectionResponse.answer === 0 ? 'fasdl' : 'facit-sp-12';
          setSpiritualData({ 
            chronicIllness: selectedTool === 'facit-sp-12',
            assessmentType: selectedTool
          });
          setAssessmentType(selectedTool);
        }
      }
      setCurrentQuestion(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Filter out the assessment selection question (id: '0') from responses
      const filteredResponses = responses.filter(response => response.questionId !== '0');
      
      // Debug: Log the data being sent
      // Submitting data
      
      const response = await fetch(`${API_CONFIG.ENDPOINTS.SPIRITUAL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          assessmentType,
          responses: filteredResponses,
          spiritualData
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Notify InsightsPage about the update
        assessmentUpdateNotifier.notifyUpdate('spiritual');
        // Navigate to completion page with assessment result
        navigate('/panel/learn/spiritual-assessment/completion', {
          state: { assessmentResult: result.data }
        });
      } else {
        const errorData = await response.json();

        if (response.status === 401) {
          alert('Your session has expired. Please log in again to continue.');
        } else {
          alert(`Failed to save assessment: ${errorData.message}`);
        }
      }
    } catch (error) {

      let errorMessage = 'Error saving spiritual assessment. Please try again.';
      if (error instanceof Error && error.message.includes('401')) {
        errorMessage = 'Your session has expired. Please log in again to continue.';
      }
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset to initial state on component mount
  useEffect(() => {
    setCurrentQuestion(1);
    setAssessmentType('fasdl');
    setSpiritualData({
      chronicIllness: false,
      assessmentType: 'fasdl'
    });
    setResponses([]);
  }, []);

  const currentQuestionData = getCurrentQuestion();
  const answerOptions = getAnswerOptions();
  const currentResponse = currentQuestionData ? responses.find(r => r.questionId === currentQuestionData.id) : null;

  if (!currentQuestionData) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div
            className="assessment-left-panel spiritual-panel"
          >
            <div className="assessment-header">
              <h1>Spiritual Assessment</h1>
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-form-page">
      <div className="assessment-container">
        {/* Left Panel - Background Image */}
        <div
          className="assessment-left-panel spiritual-panel"
        >
          <div className="assessment-header">
            <h1>Spiritual Assessment</h1>
            <p>Answer honestly to get the most accurate results</p>
          </div>
        </div>

        {/* Right Panel - Questions */}
        <div className="assessment-right-panel">
          <div className="assessment-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${(currentQuestion / totalQuestions) * 100}%` }}
              />
            </div>
            <span className="progress-text">
              Question {getDisplayQuestionNumber()} of {getDisplayTotal()}
            </span>
          </div>

          <div className="question-container">
            {currentQuestionData.type !== 'selection' && (
              <>
                <div className="category-indicator">
                  <span className="category-badge">
                    {assessmentType === 'fasdl' 
                      ? 'FASDL Spiritual Assessment'
                      : 'FACIT-Sp-12 Spiritual Assessment'}
                  </span>
                </div>
              </>
            )}
            <h2 className="question-text">{currentQuestionData.text}</h2>
            
            {assessmentType === 'fasdl' && currentQuestionData.type !== 'selection' && (
              <p className="question-instruction">
                Please indicate how much the following statements describe your daily experiences.
              </p>
            )}
            {assessmentType === 'facit-sp-12' && currentQuestionData.type !== 'selection' && (
              <p className="question-instruction">
                Please indicate how true each statement has been for you during the past 7 days.
              </p>
            )}
            
            <div className="answer-options">
              {currentQuestionData.type === 'selection' ? (
                <>
                  <p className="question-instruction">
                    Please select the assessment category you would like to take.
                  </p>
                  <div className="selection-options">
                    {(answerOptions as string[])?.map((option, index) => (
                      <button
                        key={index}
                        className={`option-button selection-button ${currentResponse?.answer === index ? 'selected' : ''}`}
                        onClick={() => saveCurrentResponse(index)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="likert-scale">
                  {(answerOptions as AnswerOption[])?.map((option, index) => (
                    <label key={index} className="likert-option">
                      <input
                        type="radio"
                        name={`question-${currentQuestionData.id}`}
                        value={option.value}
                        checked={currentResponse?.answer === option.value}
                        onChange={() => saveCurrentResponse(option.value)}
                      />
                      <span className="option-text">{option.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>


          {currentQuestionData.type !== 'selection' && (
            <div className="navigation-buttons">
              <button 
                className="btn-secondary"
                onClick={() => {
                  if (currentQuestion === 1) {
                    navigate('/panel/learn/');
                    return;
                  }
                  handleBack();
                }}
                disabled={false}
              >
                Back
              </button>
              
              <button 
                className="btn-primary"
                onClick={handleNext}
                disabled={!currentResponse}
              >
                {currentQuestion === totalQuestions ? 'Complete' : 'Next'}
              </button>
            </div>
          )}
        </div>

        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner">Submitting...</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpiritualAssessmentFormPage;



